import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, ReplaySubject, of, throwError, take } from 'rxjs';
import { catchError, map, skipWhile, startWith, switchMap, tap } from 'rxjs/operators';
import { Ticket, TicketComment, User } from './support_sdk/_internal';
import { PagedResponse, SupportService } from './support_sdk/support.service';
import { BusinessInfoService } from '../../../business-info.service';

function isNullOrUndefined(v: any): boolean {
  return v === null || v === undefined;
}

@Injectable()
export class SupportDetailsService {
  private readonly business$$: BehaviorSubject<string> = new BehaviorSubject(null);
  private readonly partnerId$$ = new ReplaySubject<string>(1);
  private readonly partnerId$ = this.partnerId$$.asObservable();

  private readonly searchTicketsRequest$$ = new BehaviorSubject({
    cursor: '',
    search: '',
    status: 'STATUS_PENDING',
    sortBy: 'SORT_BY_CREATED_AT',
    sortOrder: 'SORT_ORDER_DESC',
  });
  private readonly cursor$$: BehaviorSubject<string> = new BehaviorSubject('');
  private readonly tickets$$: BehaviorSubject<Ticket[]> = new BehaviorSubject([]);
  private readonly users$$: BehaviorSubject<User[]> = new BehaviorSubject([]);
  private readonly ticketCount$$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  private readonly loadingTickets$$: BehaviorSubject<boolean> = new BehaviorSubject(true);
  private readonly api = inject(SupportService);
  private readonly businessInfoService = inject(BusinessInfoService);

  get partnerId(): Observable<string> {
    return this.partnerId$;
  }

  public load(companyId: string): void {
    this.partnerId$
      .pipe(
        switchMap((_partnerId) => this.businessInfoService.getBusinessData(companyId)),
        catchError((error) => {
          console.error('Error loading business data', error);
          return of(null);
        }),
        take(1),
      )
      .subscribe((resp) => this.business$$.next(resp.partnerId));
  }

  updateSearch(search): void {
    this.searchTicketsRequest$$.next({
      ...this.searchTicketsRequest$$.getValue(),
      search,
      cursor: '',
    });
  }

  updateStatus(status): void {
    this.searchTicketsRequest$$.next({
      ...this.searchTicketsRequest$$.getValue(),
      status,
      cursor: '',
    });
  }

  updateSortBy(sortBy): void {
    this.searchTicketsRequest$$.next({
      ...this.searchTicketsRequest$$.getValue(),
      sortBy,
    });
  }

  updateSortOrder(sortOrder): void {
    this.searchTicketsRequest$$.next({
      ...this.searchTicketsRequest$$.getValue(),
      sortOrder,
    });
  }

  updateCursor(cursor: string): void {
    this.searchTicketsRequest$$.next({
      ...this.searchTicketsRequest$$.getValue(),
      cursor,
    });
  }

  loadTickets(cursor, search, status, sortBy, sortOrder): Observable<PagedResponse> {
    this.loadingTickets$$.next(true);
    return this.partnerId$.pipe(
      switchMap((partnerId) => this.api.searchTickets(partnerId, search, status, sortBy, sortOrder, cursor)),
      catchError((err) => {
        this.setDisplayColumnBasedOnPagedResponse(false, [], [], 0, null);
        return throwError(err);
      }),
      tap((res) => {
        this.setDisplayColumnBasedOnPagedResponse(false, res.tickets, res.users, res.count, res.nextCursor);
      }),
    );
  }

  private setDisplayColumnBasedOnPagedResponse(
    loading: boolean,
    tickets: Ticket[],
    users: User[],
    count: number,
    cursor: string,
  ): void {
    this.loadingTickets$$.next(loading);
    this.tickets$$.next(tickets);
    this.users$$.next(users);
    this.ticketCount$$.next(count);
    this.cursor$$.next(cursor);
  }

  loadTicketComments(id: any): Observable<TicketComment[]> {
    return this.api.listComments(id);
  }

  get cursor$(): Observable<string> {
    return this.cursor$$.pipe(skipWhile(isNullOrUndefined));
  }

  get tickets$(): Observable<Ticket[]> {
    return this.searchTicketsRequest$$.pipe(
      switchMap(({ cursor, search, status, sortBy, sortOrder }) =>
        this.loadTickets(cursor, search, status, sortBy, sortOrder),
      ),
      catchError(() => of(new PagedResponse([], [], null, false, 0))),
      map((res) => res.tickets),
    );
  }

  get users$(): Observable<User[]> {
    return this.users$$.pipe(skipWhile(isNullOrUndefined));
  }

  get ticketCount$(): Observable<number> {
    return this.ticketCount$$.pipe(skipWhile(isNullOrUndefined));
  }

  get business$(): Observable<string> {
    return this.business$$.pipe(skipWhile(isNullOrUndefined));
  }

  get loadingTickets$(): Observable<boolean> {
    return this.loadingTickets$$.pipe(skipWhile(isNullOrUndefined), startWith(true));
  }
}
